import { createTheme, type MantineColorsTuple } from "@mantine/core";

/**
 * Active Mantine theme (ADR-075).
 *
 * Colours stay semantic and match the Tailwind tokens in `index.css`, so
 * screens that still use utility classes and screens that use Mantine look
 * like the same product. Brand / status ladders align with the marketing
 * steel-teal palette. Status colours exist so a badge can carry a palette
 * *and* a label — never colour alone (ADR-034).
 */

const brand: MantineColorsTuple = [
  "#e8f4f6",
  "#cfe6ec",
  "#9ecdd8",
  "#6aafc0",
  "#3f91a6",
  "#2a788c",
  "#1f6478",
  "#185364",
  "#124353",
  "#0c3140",
];

const ok: MantineColorsTuple = [
  "#eaf7f1",
  "#d0ecdd",
  "#a3d9bb",
  "#6fbf93",
  "#429e6e",
  "#2d8558",
  "#26734c",
  "#1e5c3d",
  "#174830",
  "#0e3020",
];

const warn: MantineColorsTuple = [
  "#fef8eb",
  "#fceed0",
  "#f9d99a",
  "#f4c05a",
  "#e5a52e",
  "#c98b18",
  "#ab7314",
  "#875a11",
  "#66440d",
  "#432c08",
];

const danger: MantineColorsTuple = [
  "#fceeee",
  "#f6d4d6",
  "#eda8ad",
  "#e1767e",
  "#d14a55",
  "#b83642",
  "#9c2e38",
  "#7d252d",
  "#5f1c23",
  "#3f1317",
];

const plexSans = '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif';

export const mantineTheme = createTheme({
  fontFamily: plexSans,
  fontSizes: {
    xs: "0.8125rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
  },
  radius: {
    xs: "0.25rem",
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "0.75rem",
    lg: "1rem",
    xl: "1.5rem",
  },
  breakpoints: {
    xs: "36em",
    sm: "48em",
    md: "62em",
    lg: "75em",
    xl: "88em",
  },
  primaryColor: "brand",
  primaryShade: { light: 6, dark: 5 },
  colors: {
    brand,
    ok,
    warn,
    danger,
  },
  headings: {
    fontWeight: "600",
    sizes: {
      h1: { fontSize: "1.5rem", lineHeight: "1.3" },
      h2: { fontSize: "1.25rem", lineHeight: "1.35" },
      h3: { fontSize: "1.0625rem", lineHeight: "1.4" },
    },
  },
  defaultRadius: "md",
  components: {
    Button: {
      defaultProps: { size: "md" },
      styles: {
        root: {
          // Practical phone tap target; denser rows use size="compact-sm".
          fontWeight: 500,
        },
      },
    },
    Modal: {
      defaultProps: {
        centered: true,
        radius: "md",
        padding: "1.5rem",
        overlayProps: { backgroundOpacity: 0.4 },
      },
      styles: {
        // `.mantine-Modal-body` (.m_5df29311) zeroes padding-top when a header
        // is present; set the full content padding here so every dialog gets air.
        body: {
          padding: "1.25rem 1.75rem 1.75rem",
        },
      },
    },
    AppShell: {
      defaultProps: {
        padding: "md",
      },
    },
    Paper: {
      defaultProps: {
        radius: "md",
        withBorder: true,
      },
    },
    Alert: {
      defaultProps: {
        radius: "md",
      },
    },
    Badge: {
      defaultProps: {
        radius: "xl",
        size: "md",
        variant: "light",
      },
    },
    NavLink: {
      styles: {
        root: {
          borderRadius: "var(--mantine-radius-md)",
        },
      },
    },
    Select: {
      defaultProps: {
        radius: "md",
        size: "md",
        checkIconPosition: "right",
        autoComplete: "off",
      },
    },
    TextInput: {
      defaultProps: {
        autoComplete: "off",
      },
    },
    Textarea: {
      defaultProps: {
        autoComplete: "off",
      },
    },
    Input: {
      styles: {
        input: {
          // Match the app focus ring: on the control edge, not offset outside.
          "--input-bd-focus": "var(--mantine-color-brand-6)",
        },
      },
    },
  },
  other: {
    locale: "en-GB",
  },
});
